import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetUserShareQuery, useGetAllUsersQuery } from '../../../../../store/user/user'
import { useCallback, useMemo, useState } from 'react'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { transformUserFilter } from './utils'

export type UserFilterType = 'upcoming' | 'past' | 'live'

export const useUserFilter = (filterType: UserFilterType) => {
  const { hasQuota } = useZocksQuota()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')
  const { data: users, isFetching: usersLoading } = useGetAllUsersQuery(
    { accountId, identityTypes: ['idp'] },
    { refetchOnMountOrArgChange: 10, skip: !sessionSharingAvailable },
  )

  const defaultUserIds = useMemo(
    () => (filterType === 'live' ? (sessionSharingAvailable ? ['{all}'] : []) : [currentUserId]),
    [filterType, sessionSharingAvailable, currentUserId],
  )

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(defaultUserIds)
  const { data: shares, isFetching: sharedUsersLoading } = useGetUserShareQuery(
    {
      accountId: accountId,
      userId: currentUserId,
      shareType: 'meeting',
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: 10, skip: !sessionSharingAvailable },
  )

  const resetUserFilter = useCallback(() => {
    setSelectedUserIds(defaultUserIds)
  }, [defaultUserIds])

  const userFilterValues = useMemo(
    () => transformUserFilter(currentUserId, sessionSharingAvailable, filterType, users?.data ?? [], shares?.data ?? []),
    [currentUserId, sessionSharingAvailable, filterType, users, shares],
  )

  return {
    userFilterValues,
    selectedUserIds,
    setSelectedUserIds,
    resetUserFilter,
    isLoading: usersLoading || sharedUsersLoading,
    sessionSharingAvailable,
  }
}
